import type { Comparer } from "@/components/controls/grid/BmGridData";
import isEqual from "lodash-es/isEqual";
import userStore from "@/store/modules/userStore";

export const autoComparer: Comparer = (itemValueA: any, itemValueB: any) => {
  if (isEqual(itemValueA, itemValueB)) {
    return 0;
  }

  if (itemValueA === undefined) {
    return -1;
  }

  if (itemValueB === undefined) {
    return 1;
  }

  if (itemValueA === null) {
    return -1;
  }

  if (itemValueB === null) {
    return 1;
  }

  // Handler for items that implement a compareTo method (e.g. js-joda)
  if (itemValueA.compareTo && typeof itemValueA.compareTo === "function") {
    return itemValueA.compareTo(itemValueB);
  }

  // Resort to default handling (e.g. values / valueOf / toString)
  return itemValueA > itemValueB ? 1 : -1;
};

export const alphanumericComparer: Comparer = (
  itemValueA: any,
  itemValueB: any
) => {
  if (isEqual(itemValueA, itemValueB)) {
    return 0;
  }

  if (itemValueA === null || itemValueA === undefined) {
    return -1;
  }

  if (itemValueB === null || itemValueB === undefined) {
    return 1;
  }

  // See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/Collator/Collator#numeric
  // for description of numeric collation.
  // This sorts numeric portions of the strings as numbers.
  // Eg "1" < "2" < "03" < "5" < "11" < "33"
  // And "3" == "03"
  // And "A1" < "A2" < "A3" < "A11" < "A33"
  return itemValueA.localeCompare(
    itemValueB,
    userStore.state.userLanguageLocale,
    {
      sensitity: "base",
      numeric: true,
    }
  );
};

export const indexedKeyComparer =
  (keys: readonly string[], valueExtractor: (item: any) => string): Comparer =>
  (a: any, b: any) => {
    const aIndex = keys.indexOf(valueExtractor(a));
    const bIndex = keys.indexOf(valueExtractor(b));
    // Ignore if not found in the list
    if (aIndex === -1 || bIndex === -1) {
      return 0;
    }

    return aIndex - bIndex > 0 ? 1 : -1;
  };
